/* Карточка правки сущности: /edit/:kind/:id, где id = "new" для создания.
   Одна форма на все виды — поля описаны декларативно, чтобы не плодить экраны. */

import {
  accessToForm, byLadder, canEdit, crmAny, dealStagesOf, dealStreamOf, DEV_KIND_META, DEV_KINDS, devStagesOf,
  EXPENSE_CATS, GOAL_KINDS, hasAnyFunnel, INCOME_CATS, moneyEdit, MORE_BLOCKS, moreVisible, NOTIFY_KINDS,
  notifyOf, rub, streamsOf, TIER_META, TIERS, toneColor, TRANSFER,
} from "@mera/shared";
import { Button, List, ListButton, ListInput, ListItem, Segmented, SegmentedButton, Toggle } from "konsta/react";
import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "../lib/router.jsx";

import AccountsEditor from "../components/AccountsEditor.jsx";
import AvatarPicker from "../components/AvatarPicker.jsx";
import { Ion } from "../components/icons.jsx";
import DaysPicker from "../components/DaysPicker.jsx";
import ItemsEditor from "../components/ItemsEditor.jsx";
import Palette from "../components/Palette.jsx";
import SplitsEditor from "../components/SplitsEditor.jsx";
import Screen from "../components/Screen.jsx";
import { SkelForm } from "../components/skeleton.jsx";
import Attachments from "../components/Attachments.jsx";
import { Chips, ErrorState, MoneyInput } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp, useFetch } from "../lib/store.jsx";
import { alertDialog, confirmDialog, copyText, haptic } from "../lib/tg.js";

const ITEM_COLORS = {
  primaryTextIos: "text-[color:var(--tg-text)]",
  secondaryTextIos: "text-[color:var(--tg-hint)]",
};

/* Пустые строки в списках не храним — их оставляет незаполненная новая позиция */
const clean = (arr) => (arr || []).map((x) => String(x).trim()).filter(Boolean);

/* ── Описание сущностей ────────────────────────────────── */

const KINDS = {
  users: {
    adminOnly: true,
    title: "Участник",
    newTitle: "Новый участник",
    path: "/admin/users",
    blank: {
      name: "", role: "", tone: "", telegramId: "", isAdmin: false, isActive: true,
      notify: Object.fromEntries(NOTIFY_KINDS.map((k) => [k.id, true])),
      // Новый участник получает полный доступ — раздачу прав видно явно
      access: accessToForm(null),
    },
    toForm: (u) => ({
      name: u.name,
      role: u.role || "",
      tone: u.tone || "blue",
      telegramId: u.telegramId || "",
      isAdmin: !!u.isAdmin,
      isActive: !!u.isActive,
      notify: notifyOf(u),
      access: accessToForm(u.access),
    }),
    toBody: (f) => ({
      name: f.name.trim(),
      role: f.role || null,
      tone: f.tone || "blue",
      telegramId: String(f.telegramId ?? "").trim() || null,
      isAdmin: f.isAdmin,
      isActive: f.isActive,
      notify: f.notify,
      access: f.access,
    }),
  },
  businesses: {
    adminOnly: true,
    title: "Бизнес",
    newTitle: "Новый бизнес",
    path: "/admin/businesses",
    blank: {
      name: "", sub: "", tone: "", hasFunnel: true, avatar: null,
      accounts: [],
      salesStages: [], accStages: [], devStages: [],
      hasPartners: false, partnerStages: [],
      stageColors: { sales: {}, partner: {}, acc: {}, dev: {} },
    },
    toForm: (b) => ({
      name: b.name,
      sub: b.sub || "",
      avatar: b.avatar || null,
      // Цвет бизнеса теперь сам цвет, а не название: старые записи переводит toneColor
      tone: toneColor(b.tone, ""),
      hasFunnel: !!b.hasFunnel,
      accounts: (b.accounts || []).map((a) => ({ id: a.id, name: a.name, balance: a.balance })),
      salesStages: b.salesStages || [],
      accStages: b.accStages || [],
      devStages: b.devStages || [],
      // Воронка партнёров необязательная: заведена, пока у неё есть этапы
      hasPartners: !!b.partnerStages?.length,
      partnerStages: b.partnerStages || [],
      stageColors: { sales: {}, partner: {}, acc: {}, dev: {}, ...(b.stageColors || {}) },
    }),
    toBody: (f) => ({
      name: f.name.trim(),
      sub: f.sub || null,
      avatar: f.avatar,
      tone: f.tone || null,
      hasFunnel: f.hasFunnel,
      // Счета уходят вместе с бизнесом: своей сущности у них нет
      accounts: (f.accounts || []).filter((a) => String(a.name || "").trim()),
      salesStages: f.hasFunnel ? clean(f.salesStages) : [],
      accStages: f.hasFunnel ? clean(f.accStages) : [],
      // Включили без этапов — этапы по умолчанию подставит сервер
      hasPartners: f.hasPartners,
      partnerStages: f.hasPartners ? clean(f.partnerStages) : [],
      devStages: clean(f.devStages),
      stageColors: f.stageColors || {},
    }),
  },
  /* Ступень лестницы целей. Сколько уже набрано, форма не спрашивает: и деньги
     на счетах, и доход в месяц считает сервер — руками такое не правят. */
  goals: {
    adminOnly: true,
    title: "Цель",
    newTitle: "Новая цель",
    path: "/admin/goals",
    blank: { name: "", kind: "balance", goal: "", accounts: [], color: "", position: "" },
    toForm: (g, { meta } = {}) => ({
      name: g.name,
      kind: g.kind || "balance",
      goal: g.goal ?? "",
      accounts: g.accounts || [],
      color: g.color || "",
      // Место в лестнице — это позиция цели в текущем порядке, а не хранимое поле
      position: String([...(meta?.goals || [])].sort(byLadder).findIndex((x) => x.id === g.id) + 1),
    }),
    toBody: (f) => ({
      name: f.name.trim(),
      kind: f.kind,
      goal: Number(f.goal) || 0,
      // Доход в месяц считается по всему журналу — счета к нему не привязываются
      accounts: f.kind === "income" ? [] : f.accounts,
      color: f.color || null,
      position: f.position ? Number(f.position) : null,
    }),
  },
  recurring: {
    adminOnly: true,
    title: "Регулярный расход",
    newTitle: "Новый расход",
    path: "/admin/recurring",
    blank: { name: "", splits: [], days: [], note: "" },
    toForm: (r) => ({
      name: r.name,
      splits: (r.splits || []).map((x) => ({ acc: x.acc, amount: x.amount })),
      days: r.days || [],
      note: r.note || "",
    }),
    toBody: (f) => ({
      name: f.name.trim(),
      splits: (f.splits || []).filter((s) => s.acc && Number(s.amount)),
      days: f.days || [],
      note: f.note || null,
    }),
  },
  payments: {
    title: "Ожидаемое поступление",
    newTitle: "Новое поступление",
    path: "/payments",
    access: moneyEdit,
    required: "client",
    blank: ({ today }) => ({
      client: "", businessId: "", amount: "", profit: "", kind: "",
      dueDate: today, received: false, note: "",
      account: "", paidDate: today,
    }),
    toForm: (p, { today } = {}) => ({
      client: p.client || "",
      businessId: p.businessId || "",
      amount: p.amount ?? "",
      profit: p.profit ?? "",
      kind: p.kind || "",
      dueDate: p.dueDate || today || "",
      received: p.status === "received",
      note: p.note || "",
      account: "",
      paidDate: today || "",
    }),
    toBody: (f) => ({
      client: f.client.trim(),
      businessId: f.businessId || null,
      // Воронка и бизнес — одно и то же направление; прогноз разбирается по ней
      funnel: f.businessId || null,
      amount: Number(f.amount) || 0,
      profit: Number(f.profit) || 0,
      kind: f.kind || null,
      dueDate: f.dueDate || null,
      status: f.received ? "received" : null,
      note: f.note || null,
    }),
  },
  inflow: {
    title: "Приток за месяц",
    newTitle: "Приток за месяц",
    path: "/inflow",
    access: moneyEdit,
    required: "month",
    blank: { month: "", v: "" },
    toForm: (m) => ({ month: m.month, v: m.v ?? "" }),
    toBody: (f) => ({ month: String(f.month).trim(), v: Number(f.v) || 0 }),
  },
  operations: {
    title: "Операция",
    newTitle: "Новая операция",
    path: "/operations",
    access: moneyEdit,
    // Включённый тоггл превращает запись в ожидаемое поступление — это другой ресурс
    pathOf: (f) => (f.upcoming ? "/payments" : "/operations"),
    removeText: (f) =>
      `${f.split ? "платёж" : "операцию"} «${f.note.trim() || f.cat || "без описания"}» на ${rub(f.amount)}` +
      (f.split ? ` со всех ${f.splits.length} счетов` : ""),
    validate: (f) =>
      !(Number(f.amount) > 0) ? "Укажите сумму больше нуля"
        : f.upcoming && !f.note.trim() ? "Опишите, за что ждёте деньги"
          : !f.upcoming && f.split ? splitProblem(f)
          : !f.upcoming && !f.account ? "Выберите счёт"
            : f.type === TRANSFER && !f.to ? "Выберите счёт, куда переводим"
              : f.type === TRANSFER && f.to === f.account ? "Счета перевода должны отличаться"
                : null,
    toForm: (o) => ({
      date: o.date,
      type: o.type,
      amount: o.amount ?? "",
      account: o.accountId || "",
      to: o.toAccountId || "",
      cat: o.cat || "",
      proj: o.proj || "",
      note: o.note || "",
      upcoming: false,
      // Составной платёж: доли по счетам. wasSplit помнит, что разбивку надо снять, если её выключили
      split: (o.splits || []).length > 1,
      wasSplit: (o.splits || []).length > 1,
      splits: (o.splits || []).map((p) => ({ acc: p.accountId, amount: String(p.amount) })),
    }),
    blank: ({ today, params }) => ({
      date: today,
      type: "Расход",
      amount: "",
      account: "",
      to: "",
      cat: "",
      proj: params.get("proj") || "",
      note: "",
      upcoming: params.get("upcoming") === "1",
      split: false,
      wasSplit: false,
      splits: [],
    }),
    toBody: (f) =>
      f.upcoming
        ? {
          // Клиента отдельным полем не спрашиваем — описание и есть название платежа
          client: f.note.trim(),
          amount: Number(f.amount) || 0,
          /* Дата операции здесь означает, когда денег ждём. Уходит она датой
             (dueDate), а не свободным «когда» (when): в when платёж не хранил дату
             вовсе, и при открытии форма подставляла сегодняшний день */
          dueDate: f.date,
          note: f.note || null,
        }
        : {
          date: f.date,
          type: f.type,
          amount: Number(f.amount) || 0,
          // Счёт задаёт и бизнес операции — отдельного поля под него нет
          account: f.account,
          // У перевода второй счёт — получатель; у дохода и расхода его нет
          to: f.type === TRANSFER ? f.to : null,
          // Деятельность из формы убрана: почти всё операционное, разносим вручную при разборе
          act: "Операционная",
          cat: f.type === TRANSFER ? null : f.cat || null,
          proj: f.type === TRANSFER ? null : f.proj || null,
          note: f.note || null,
          /* Доли платежа. Разбивку выключили у составного — уходит одна доля,
             и сервер собирает платёж обратно в обычную операцию */
          ...(f.split && f.type !== TRANSFER
            ? { account: undefined, splits: f.splits.map((p) => ({ account: p.acc, amount: Number(p.amount) })) }
            : f.wasSplit ? { splits: [{ account: f.account, amount: Number(f.amount) || 0 }] } : {}),
        },
  },
  /* Правка проекта: открывается из его карточки — своего раздела у проектов нет,
     они заводятся вместе со сделкой. Создание тут осталось только для API и бота. */
  projects: {
    adminOnly: true,
    title: "Проект",
    newTitle: "Новый проект",
    // Заводится под админом, а правится и удаляется общими маршрутами проектов
    postPath: "/admin/projects",
    deletePath: "/admin/projects",
    path: "/projects",
    // Удалили проект — возвращаться на его же карточку некуда, уходим на шаг дальше
    afterDelete: (navigate) => navigate(-2),
    blank: { name: "", kind: "agency", businessId: "", type: "", owner: "", income: "", expenses: "", volume: "", avatar: null },
    toForm: (p) => ({
      name: p.name,
      avatar: p.avatar || null,
      kind: p.kind || "agency",
      businessId: p.businessId || "",
      type: p.type || "",
      owner: p.owner || "",
      income: p.income ?? "",
      expenses: p.expenses ?? "",
      volume: p.volume ?? "",
    }),
    toBody: (f) => ({
      name: f.name.trim(),
      avatar: f.avatar,
      kind: f.kind,
      businessId: f.businessId || null,
      type: f.type || null,
      owner: f.owner || null,
      income: Number(f.income) || 0,
      expenses: Number(f.expenses) || 0,
      volume: f.volume === "" ? null : Number(f.volume),
    }),
  },
  /* Сделка = проект: одна форма заводит обе записи разом, поэтому здесь и поля
     продажи, и экономика работы. Проект создаёт сервер тем же запросом — стадии
     аккаунтинга у него нет, в ту воронку он попадает кнопкой из карточки сделки. */
  deals: {
    title: "Сделка",
    newTitle: "Новая сделка",
    path: "/deals",
    access: (me) => crmAny(me, canEdit),
    required: "company",
    blank: ({ params, meta }) => ({
      company: "",
      // Продажи или партнёры: форма одна, различается только набор этапов
      stream: dealStreamOf({ stream: params.get("stream") }),
      funnel: params.get("funnel")
        || meta?.businesses?.find((b) => dealStagesOf(b, dealStreamOf({ stream: params.get("stream") })).length)?.id
        || "",
      owner: "",
      tier: "",
      lpr: "",
      src: "",
      projType: "",
      revenue: "",
      expenses: "",
      mrr: "",
      // Контакт: "" — не нужен, "new" — вписывают нового, иначе id существующего
      contactMode: "",
      contactName: "",
      contactRole: "",
      contactTg: "",
      contactPhone: "",
      contactEmail: "",
    }),
    /* Правка — той же формой: карточка сделки ведёт сюда. Себестоимость не
       хранится отдельно — восстанавливаем её как чек минус прибыль. */
    toForm: (d) => ({
      company: d.company,
      stream: dealStreamOf(d),
      funnel: d.funnel,
      owner: d.owner || "",
      tier: d.tier || "",
      lpr: d.lpr || "",
      src: d.src || "",
      projType: d.projType || "",
      revenue: d.revenue ?? "",
      expenses: d.revenue != null ? Math.max(0, (d.revenue || 0) - (d.profit || 0)) : "",
      mrr: d.mrr ?? "",
      contactMode: "",
      contactName: "",
      contactRole: "",
      contactTg: "",
      contactPhone: "",
      contactEmail: "",
    }),
    toBody: (f) => ({
      company: f.company.trim(),
      // Воронку сервер берёт только при создании — у живой сделки она постоянна
      stream: f.stream,
      funnel: f.funnel,
      owner: f.owner || null,
      // Тир не выбран — умолчание ставит сервер, а не форма
      tier: f.tier || undefined,
      lpr: f.lpr || null,
      src: f.src || null,
      projType: f.projType || null,
      // Чек сделки он же чек проекта; прибыль — чек минус себестоимость
      revenue: Number(f.revenue) || 0,
      expenses: Number(f.expenses) || 0,
      profit: (Number(f.revenue) || 0) - (Number(f.expenses) || 0),
      mrr: f.mrr === "" ? null : Number(f.mrr),
      // Существующий контакт тоже уходит полями: у сделки появляется своя копия
      contact: f.contactMode && f.contactName.trim()
        ? {
          name: f.contactName,
          role: f.contactRole || null,
          tg: f.contactTg || null,
          phone: f.contactPhone || null,
          email: f.contactEmail || null,
          links: f.contactLinks || [],
        }
        : undefined,
    }),
    // Заведённая сделка сразу открывается карточкой — там её и ведут дальше
    afterCreate: (created, navigate) => navigate(`/deal/${created.id}`, { replace: true }),
  },
  /* Контакт сделки: /edit/contacts/:id?deal=. Своего раздела у контактов нет —
     они живут в карточке сделки, поэтому и путь API строится от неё */
  contacts: {
    title: "Контакт",
    newTitle: "Новый контакт",
    pathOf: (f) => `/deals/${f.dealId}/contacts`,
    access: (me) => crmAny(me, canEdit),
    blank: ({ params }) => ({ dealId: params.get("deal") || "", name: "", role: "", tg: "", phone: "", email: "", links: [] }),
    toForm: (c, { params }) => ({
      dealId: params.get("deal") || "",
      name: c.name || "",
      role: c.role || "",
      tg: c.tg || "",
      phone: c.phone || "",
      email: c.email || "",
      links: c.links || [],
    }),
    toBody: (f) => ({
      name: f.name.trim(),
      role: f.role.trim() || null,
      tg: f.tg.trim() || null,
      phone: f.phone.trim() || null,
      email: f.email.trim() || null,
      links: clean(f.links),
    }),
  },
  devTasks: {
    title: "Карточка разработки",
    newTitle: "Новая карточка",
    path: "/dev-tasks",
    access: (me) => crmAny(me, canEdit),
    required: "title",
    blank: ({ today, params }) => ({
      title: "",
      businessId: params.get("biz") || "",
      kind: "",
      stage: params.get("stage") || "",
      projectId: "",
      who: "",
      dueDate: today,
      details: "",
      // Файлы, выбранные до сохранения: карточки ещё нет, привязать их не к чему
      atts: [],
    }),
    toForm: (t) => ({
      title: t.title,
      businessId: t.businessId || "",
      kind: t.kind || "",
      stage: t.stage || "",
      projectId: t.projectId || "",
      who: t.who || "",
      dueDate: t.dueDate || "",
      details: t.details || "",
    }),
    toBody: (f) => ({
      title: f.title.trim(),
      businessId: f.businessId || null,
      kind: f.kind || undefined,
      stage: f.stage || undefined,
      projectId: f.projectId || null,
      who: f.who || null,
      dueDate: f.dueDate || null,
      details: f.details || null,
    }),
    /* Файлы прикрепляют прямо в форме, но привязать их можно только к готовой
       карточке — поэтому после создания их досылают сюда */
    attach: (created) => `/dev-tasks/${created.id}`,
    // Заведённая карточка сразу открывается — там чек-лист и вложения
    afterCreate: (created, navigate) => navigate(`/devtask/${created.id}`, { replace: true }),
  },
  passwords: {
    title: "Доступ",
    newTitle: "Новый доступ",
    path: "/passwords",
    // Хранилище закрыто участнику — закрыта и правка его записей
    access: (me) => moreVisible(me, "vault"),
    blank: { name: "", url: "", login: "", password: "" },
    toForm: (p) => ({
      name: p.name,
      url: p.url || "",
      login: p.login || "",
      password: p.password || "",
    }),
    toBody: (f) => ({
      name: f.name.trim(),
      url: f.url.trim() || null,
      login: f.login.trim() || null,
      password: f.password || null,
    }),
  },
  cards: {
    title: "Карта",
    newTitle: "Новая карта",
    path: "/cards",
    access: (me) => moreVisible(me, "vault"),
    required: "number",
    blank: { number: "", year: "", cvv: "", holder: "" },
    toForm: (c) => ({
      number: c.number,
      year: c.year || "",
      cvv: c.cvv || "",
      holder: c.holder || "",
    }),
    toBody: (f) => ({
      number: f.number.trim(),
      year: f.year.trim() || null,
      cvv: f.cvv.trim() || null,
      holder: f.holder.trim() || null,
    }),
  },
};

/* Что не так с разбивкой платежа — или null, если доли сходятся */
const cents = (v) => Math.round((Number(v) || 0) * 100);
function splitProblem(f) {
  const rows = f.splits || [];
  if (rows.length < 2) return "Добавьте хотя бы два счёта или выключите разбивку";
  if (rows.some((r) => !r.acc)) return "Выберите счёт у каждой доли";
  if (new Set(rows.map((r) => r.acc)).size !== rows.length) return "Счёт встречается в разбивке дважды";
  if (rows.some((r) => !(Number(r.amount) > 0))) return "У каждой доли нужна сумма больше нуля";
  const diff = cents(f.amount) - rows.reduce((s, r) => s + cents(r.amount), 0);
  if (diff > 0) return `Осталось разложить ${rub(diff / 100)}`;
  if (diff < 0) return `Доли больше платежа на ${rub(-diff / 100)}`;
  return null;
}

/* ── Экран ─────────────────────────────────────────────── */

export default function EntityEdit() {
  const { kind, id } = useParams();
  const { me, meta, today, reload } = useApp();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const spec = KINDS[kind];
  const isNew = id === "new";

  // Участники приходят отдельным запросом, бизнесы со счетами уже лежат в справочниках
  const users = useFetch(kind === "users" && !isNew ? "/admin/users" : null);
  const projects = useFetch(kind === "projects" || kind === "devTasks" ? "/projects" : null);
  const operation = useFetch(kind === "operations" && !isNew ? `/operations/${id}` : null);
  const deal = useFetch(kind === "deals" && !isNew ? `/deals/${id}` : null);
  // Контакт отдельно не отдаётся — берём его из сделки, в которой он живёт
  const contactDeal = useFetch(kind === "contacts" && !isNew ? `/deals/${params.get("deal")}` : null);
  const devTask = useFetch(kind === "devTasks" && !isNew ? `/dev-tasks/${id}` : null);
  const passwords = useFetch(kind === "passwords" && !isNew ? "/passwords" : null);
  const cards = useFetch(kind === "cards" && !isNew ? "/cards" : null);

  const source =
    isNew ? null
      : kind === "users" ? (users.data || []).find((u) => String(u.id) === String(id))
        : kind === "projects" ? (projects.data || []).find((p) => p.id === id)
          : kind === "operations" ? operation.data
        : kind === "deals" ? deal.data
        : kind === "contacts" ? (contactDeal.data?.contacts || []).find((c) => String(c.id) === String(id))
        : kind === "devTasks" ? devTask.data
        : kind === "passwords" ? (passwords.data || []).find((p) => String(p.id) === String(id))
        : kind === "cards" ? (cards.data || []).find((c) => String(c.id) === String(id))
        : kind === "businesses" ? (meta?.businesses || []).find((b) => b.id === id)
          : kind === "goals" ? (meta?.goals || []).find((g) => String(g.id) === String(id))
            : kind === "recurring" ? (meta?.recurring || []).find((r) => String(r.id) === String(id))
              : kind === "payments" ? (meta?.payments || []).find((p) => String(p.id) === String(id))
                : kind === "inflow" ? (meta?.monthlyInflow || []).find((m) => m.month === id)
                  : null;

  const [f, setF] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  // Форма заполняется один раз — когда данные приехали
  React.useEffect(() => {
    if (f || !spec) return;
    if (isNew) setF(typeof spec.blank === "function" ? spec.blank({ today, params, meta }) : spec.blank);
    else if (source) setF(spec.toForm(source, { today, meta, params }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, spec, isNew, source]);

  if (!spec) return <Screen back><ErrorState text="Неизвестный раздел." /></Screen>;
  if (spec.adminOnly && !me?.isAdmin) return <Screen back><ErrorState text="Раздел доступен только администраторам." /></Screen>;
  if (spec.access && !spec.access(me)) {
    return <Screen back><ErrorState text="У вас доступ только на чтение — правки здесь закрыты." /></Screen>;
  }
  if (kind === "users" && !isNew && users.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (kind === "projects" && !isNew && projects.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (kind === "operations" && !isNew && operation.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (kind === "deals" && !isNew && deal.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (kind === "contacts" && !isNew && contactDeal.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (kind === "devTasks" && !isNew && devTask.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (kind === "passwords" && !isNew && passwords.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (kind === "cards" && !isNew && cards.loading) return <Screen back title={spec.title}><SkelForm /></Screen>;
  if (!isNew && !source) return <Screen back><ErrorState text="Запись не найдена." /></Screen>;
  if (!f) return <Screen back title={isNew ? spec.newTitle : spec.title}><SkelForm /></Screen>;

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setList = (k) => (next) => setF((p) => ({ ...p, [k]: next }));
  const toggle = (k) => () => { haptic.select(); setF((p) => ({ ...p, [k]: !p[k] })); };

  const save = async () => {
    const problem = spec.validate
      ? spec.validate(f)
      : String(f[spec.required || "name"] || "").trim() ? null : "Заполните первое поле";
    if (problem) return setErr(problem);
    setBusy(true);
    setErr(null);
    try {
      const body = spec.toBody(f);
      const path = spec.pathOf ? spec.pathOf(f) : spec.path;
      const created = isNew
        ? await api.post(spec.postPath || path, body)
        : await api.patch(`${path}/${id}`, body);
      /* Вложения, выбранные до сохранения: файлы уже лежат на сервере, осталось
         привязать их к заведённой записи — раньше её просто не существовало */
      if (isNew && spec.attach && f.atts?.length) {
        const base = spec.attach(created);
        for (const a of f.atts) await api.post(`${base}/attachments`, a);
      }
      haptic.success();
      reload();
      if (isNew && spec.afterCreate) spec.afterCreate(created, navigate);
      else navigate(-1);
    } catch (e) {
      haptic.error();
      // Сервер умеет перечислять, что именно не так — показываем это, а не общее «проверьте поля»
      setErr(e.details?.length ? e.details.join(" · ") : e.message);
      setBusy(false);
    }
  };

  /* Плановое поступление стало фактом: сервер заводит операцию и закрывает план.
     Сначала сохраняем правки формы — сумму и клиента берут из неё. */
  const markPaid = async () => {
    const ok = await confirmDialog(`Записать поступление ${f.amount || 0} ₽ от «${f.client}» в журнал?`);
    if (!ok) return;
    setBusy(true);
    setErr(null);
    try {
      await api.patch(`/payments/${id}`, spec.toBody(f));
      await api.post(`/payments/${id}/paid`, {
        account: f.account || null,
        date: f.paidDate,
        amount: Number(f.amount) || 0,
      });
      haptic.success();
      reload();
      navigate(-1);
    } catch (e) {
      haptic.error();
      setErr(e.message);
      setBusy(false);
    }
  };

  const remove = async () => {
    /* Обычно запись зовут по имени, но у операции его нет — спрашиваем её словами:
       журнал теперь открывается нажатием, и удаляют операцию только отсюда. */
    const what = spec.removeText ? spec.removeText(f) : `«${f[spec.required || "name"]}»`;
    const ok = await confirmDialog(`Удалить ${what}?`);
    if (!ok) return;
    try {
      await api.del(`${spec.deletePath || (spec.pathOf ? spec.pathOf(f) : spec.path)}/${id}`);
      haptic.success();
      reload();
      // Экран, с которого пришли, мог жить самой записью — тогда возвращаться на него некуда
      if (spec.afterDelete) spec.afterDelete(navigate);
      else navigate(-1);
    } catch (e) {
      haptic.error();
      alertDialog(e.message);
    }
  };

  return (
    <Screen back title={isNew ? spec.newTitle : spec.title}>
      {kind === "users" ? (
        <UserFields f={f} set={set} setList={setList} toggle={toggle} isNew={isNew} meta={meta} />
      ) : null}
      {kind === "businesses" ? <BusinessFields f={f} set={set} setList={setList} toggle={toggle} /> : null}
      {kind === "goals" ? (
        <GoalFields f={f} set={set} setList={setList} meta={meta} isNew={isNew} />
      ) : null}
      {kind === "recurring" ? <RecurringFields f={f} set={set} setList={setList} meta={meta} /> : null}
      {kind === "payments" ? (
        <PaymentFields f={f} set={set} meta={meta} isNew={isNew} onPaid={markPaid} busy={busy} />
      ) : null}
      {kind === "inflow" ? <InflowFields f={f} set={set} isNew={isNew} /> : null}
      {kind === "deals" ? <DealFields f={f} set={set} setF={setF} meta={meta} isNew={isNew} /> : null}
      {kind === "devTasks" ? (
        <DevTaskFields f={f} set={set} setF={setF} meta={meta} projects={projects.data || []} isNew={isNew} />
      ) : null}
      {kind === "projects" ? <ProjectFields f={f} set={set} setList={setList} meta={meta} /> : null}
      {kind === "contacts" ? <ContactFields f={f} set={set} setList={setList} /> : null}
      {kind === "passwords" ? <PasswordFields f={f} set={set} /> : null}
      {kind === "cards" ? <CardFields f={f} set={set} /> : null}
      {kind === "operations" ? (
        <OperationFields f={f} set={set} setF={setF} toggle={toggle} meta={meta} canSwitch={isNew} />
      ) : null}

      {err ? (
        <div className="px-4 pt-1 text-center text-[13px]" style={{ color: "var(--fin-red)" }}>{err}</div>
      ) : null}

      <div className="px-4 pt-3">
        <Button large rounded onClick={save} disabled={busy}>
          {isNew ? "Создать" : "Сохранить"}
        </Button>
      </div>

      {!isNew ? (
        <List strong inset>
          <ListButton colors={{ textIos: "text-[color:var(--fin-red)]" }} onClick={remove}>
            Удалить
          </ListButton>
        </List>
      ) : null}
    </Screen>
  );
}

/* ── Поля ──────────────────────────────────────────────── */

/* Поля без подписей — «Only Inputs» из Konsta, роль подписи играет placeholder.
   В выпадающих списках placeholder не виден, поэтому подпись зашита в сами варианты. */
const TONES = [
  ["blue", "Цвет · синий"],
  ["green", "Цвет · зелёный"],
  ["gold", "Цвет · золотой"],
  ["red", "Цвет · красный"],
];

function UserFields({ f, set, setList, toggle, isNew, meta }) {
  const toggleNotify = (id) => () => {
    haptic.select();
    setList("notify")({ ...f.notify, [id]: !f.notify?.[id] });
  };

  return (
    <>
      <List strong inset>
        <ListInput type="text" placeholder="Имя" value={f.name} onChange={set("name")} />
        <ListInput type="text" placeholder="Роль · продажи, маркетинг" value={f.role} onChange={set("role")} />
        <ListInput type="number" inputMode="numeric" placeholder="Telegram ID · напр. 467257161"
          value={f.telegramId} onChange={set("telegramId")} />
        <ListInput type="select" value={f.tone} onChange={set("tone")} dropdown>
          <option value="">Цвет не выбран</option>
          {TONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </ListInput>
      </List>

      <List strong inset>
        <ListItem
          colors={ITEM_COLORS}
          title="Администратор"
          after={<Toggle checked={f.isAdmin} onChange={toggle("isAdmin")} />}
        />
        {!isNew ? (
          <ListItem
            colors={ITEM_COLORS}
            title="Доступ включён"
            after={<Toggle checked={f.isActive} onChange={toggle("isActive")} />}
          />
        ) : null}
      </List>

      <AccessFields f={f} setList={setList} meta={meta} />

      {/* Выключенный вид уведомлений не приходит в бота и не показывается человеку
          в мини-аппе — ни в алертах, ни в счётчиках */}
      <div className="px-8 pt-6 pb-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        Уведомления
      </div>
      <List strong inset className="!my-0">
        {NOTIFY_KINDS.map((k) => (
          <ListItem
            key={k.id}
            colors={ITEM_COLORS}
            title={k.label}
            after={<Toggle checked={f.notify?.[k.id] !== false} onChange={toggleNotify(k.id)} />}
          />
        ))}
      </List>
    </>
  );
}

/* ── Права по разделам ──────────────────────────────────────
   Главная и финансы — уровень целиком; алерты только показать/скрыть.
   CRM гибче всех: общий уровень, поверх него бизнесы, поверх бизнеса — воронки.
   Что не переопределено, наследуется сверху вниз. У админа прав нет — у него всё. */

const LEVEL_LABELS = { edit: "редактирование", read: "только чтение", none: "скрыто" };

/** Подпись раздела + уровень в самих вариантах: placeholder в select не виден */
const LevelSelect = ({ label, value, onChange, options }) => (
  <ListInput type="select" value={value} onChange={onChange} dropdown>
    {options.map(([v, l]) => (
      <option key={v} value={v}>{label} · {l}</option>
    ))}
  </ListInput>
);

const FULL_LEVELS = [["edit", LEVEL_LABELS.edit], ["read", LEVEL_LABELS.read], ["none", LEVEL_LABELS.none]];

function AccessFields({ f, setList, meta }) {
  // Админа права не касаются: настраивать нечего, блок только сбивал бы с толку
  if (f.isAdmin) return null;

  const a = f.access || accessToForm(null);
  const setA = (patch) => setList("access")({ ...a, ...patch });
  const pick = (key) => (e) => { haptic.select(); setA({ [key]: e.target.value }); };

  /* Переопределение бизнеса: "" — как вся CRM (запись убирается), уровень строкой —
     бизнес целиком, "custom" — раскрыть воронки и настраивать каждую */
  const setBiz = (bizId) => (e) => {
    haptic.select();
    const v = e.target.value;
    const next = { ...(a.crmBiz || {}) };
    if (v === "") delete next[bizId];
    else if (v === "custom") next[bizId] = typeof next[bizId] === "object" ? next[bizId] : {};
    else next[bizId] = v;
    setA({ crmBiz: next });
  };

  const setStream = (bizId, stream) => (e) => {
    haptic.select();
    const v = e.target.value;
    const cur = typeof a.crmBiz?.[bizId] === "object" ? { ...a.crmBiz[bizId] } : {};
    if (v === "") delete cur[stream];
    else cur[stream] = v;
    setA({ crmBiz: { ...(a.crmBiz || {}), [bizId]: cur } });
  };

  const businesses = (meta?.businesses || []).filter(hasAnyFunnel);

  return (
    <>
      <div className="px-8 pt-6 pb-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        Доступ к разделам
      </div>
      <List strong inset className="!my-0">
        <LevelSelect label="Главная" value={a.overview} onChange={pick("overview")} options={FULL_LEVELS} />
        <LevelSelect label="Финансы" value={a.finance} onChange={pick("finance")} options={FULL_LEVELS} />
        <ListItem
          colors={ITEM_COLORS}
          title="Алерты"
          after={
            <Toggle
              checked={a.alerts !== "none"}
              onChange={() => { haptic.select(); setA({ alerts: a.alerts === "none" ? "read" : "none" }); }}
            />
          }
        />
      </List>
      <div className="px-8 pt-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        «Только чтение» прячет кнопки добавления и правок; «скрыто» убирает раздел
        из нижнего меню целиком.
      </div>

      <div className="px-8 pt-6 pb-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        CRM
      </div>
      <List strong inset className="!my-0">
        <LevelSelect label="Вся CRM" value={a.crm} onChange={pick("crm")} options={FULL_LEVELS} />
      </List>

      {/* Каждый бизнес с воронкой можно перебить отдельно, а внутри — по воронкам */}
      {businesses.map((b) => {
        const v = a.crmBiz?.[b.id];
        const value = v == null ? "" : typeof v === "string" ? v : "custom";
        return (
          <List strong inset key={b.id} className="!my-3">
            <LevelSelect
              label={b.name}
              value={value}
              onChange={setBiz(b.id)}
              options={[["", "как вся CRM"], ...FULL_LEVELS, ["custom", "по воронкам"]]}
            />
            {value === "custom"
              ? streamsOf(b).map((s) => (
                <LevelSelect
                  key={s.id}
                  label={`${s.l}`}
                  value={typeof v === "object" ? v[s.id] || "" : ""}
                  onChange={setStream(b.id, s.id)}
                  options={[["", "как вся CRM"], ...FULL_LEVELS]}
                />
              ))
              : null}
          </List>
        );
      })}
      <div className="px-8 -mt-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        Не переопределённое наследуется: воронка ← бизнес ← вся CRM. Если человеку
        не оставить ни одной воронки, раздел CRM пропадёт из меню.
      </div>

      {/* Блоки «Ещё» — тогглами: уровней у них нет, блок либо есть, либо нет */}
      <div className="px-8 pt-6 pb-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        Раздел «Ещё»
      </div>
      <List strong inset className="!my-0">
        {MORE_BLOCKS.map((b) => (
          <ListItem
            key={b.id}
            colors={ITEM_COLORS}
            title={b.label}
            after={
              <Toggle
                checked={a.more?.[b.id] !== false}
                onChange={() => {
                  haptic.select();
                  setA({ more: { ...(a.more || {}), [b.id]: a.more?.[b.id] === false } });
                }}
              />
            }
          />
        ))}
      </List>
      <div className="px-8 pt-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        Сам раздел «Ещё» остаётся у всех: в нём профиль и выбор темы.
      </div>
    </>
  );
}

function BusinessFields({ f, set, setList, toggle }) {
  /* Цвета этапов лежат одним полем, разложенным по воронкам — правим нужную ветку */
  const setColors = (scope) => (next) =>
    setList("stageColors")({ ...f.stageColors, [scope]: next });

  return (
    <>
      <AvatarPicker value={f.avatar} onChange={setList("avatar")} />

      <List strong inset>
        <ListInput type="text" placeholder="Название" value={f.name} onChange={set("name")} />
        <ListInput type="text" placeholder="Подпись · агентство, наш актив" value={f.sub} onChange={set("sub")} />
        {/* Цвет выбирается сам, а не по названию: им красится карточка бизнеса
            в обзоре и кружок его воронки */}
        <li>
          <Palette
            className=""
            value={f.tone}
            onPick={(c) => { haptic.select(); setList("tone")(c || ""); }}
          />
        </li>
      </List>

      {/* Счета — часть бизнеса, а не отдельный справочник: заводятся здесь же */}
      <AccountsEditor accounts={f.accounts} onChange={setList("accounts")} />

      <List strong inset>
        <ListItem
          colors={ITEM_COLORS}
          title="Своя воронка продаж"
          after={<Toggle checked={f.hasFunnel} onChange={toggle("hasFunnel")} />}
        />
      </List>

      {f.hasFunnel ? (
        <>
          <ItemsEditor
            caption="Этапы воронки"
            placeholder="Название этапа"
            ordered
            items={f.salesStages}
            onChange={setList("salesStages")}
            colors={f.stageColors?.sales || {}}
            onColorsChange={setColors("sales")}
          />
          <ItemsEditor
            caption="Стадии аккаунтинга"
            placeholder="Название стадии"
            ordered
            items={f.accStages}
            onChange={setList("accStages")}
            colors={f.stageColors?.acc || {}}
            onColorsChange={setColors("acc")}
          />
        </>
      ) : null}

      {/* Партнёры — необязательная воронка: те же карточки сделок со своими этапами.
          Выключенная прячется из CRM, сами сделки при этом не удаляются. */}
      <List strong inset>
        <ListItem
          colors={ITEM_COLORS}
          title="Воронка партнёров"
          after={<Toggle checked={f.hasPartners} onChange={toggle("hasPartners")} />}
        />
      </List>

      {f.hasPartners ? (
        <ItemsEditor
          caption="Этапы партнёров"
          placeholder="Название этапа"
          ordered
          items={f.partnerStages}
          onChange={setList("partnerStages")}
          colors={f.stageColors?.partner || {}}
          onColorsChange={setColors("partner")}
        />
      ) : null}

      {/* Разработка не зависит от продаж: доска фич и багов бывает и у продукта.
          Пустой список — воронки в CRM нет. */}
      <ItemsEditor
        caption="Этапы разработки"
        placeholder="Название этапа"
        ordered
        items={f.devStages}
        onChange={setList("devStages")}
        colors={f.stageColors?.dev || {}}
        onColorsChange={setColors("dev")}
      />
    </>
  );
}

/* Цель: что считаем, до какой планки, каким цветом и какой ступенью лестницы.
   Счета выбираются только у накопительной цели — доход в месяц считается по всему
   журналу целиком. Ничего не выбрано — цель смотрит на все деньги группы. */
function GoalFields({ f, set, setList, meta, isNew }) {
  const accounts = meta?.accounts || [];
  const count = (meta?.goals || []).length;
  // Новая цель может встать и в конец, поэтому мест на одно больше
  const places = Array.from({ length: isNew ? count + 1 : count }, (_, i) => i + 1);

  const toggleAccount = (id) => () => {
    haptic.select();
    const list = f.accounts || [];
    setList("accounts")(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  return (
    <>
      <List strong inset>
        <ListInput type="text" placeholder="Название цели · общак, НЗ" value={f.name} onChange={set("name")} />
        <ListInput
          type="number"
          inputMode="decimal"
          placeholder={f.kind === "income" ? "Сколько зарабатывать в месяц, ₽" : "Сколько накопить, ₽"}
          value={f.goal}
          onChange={set("goal")}
        />
        {/* Цветом кольца цель отличается в лестнице обзора */}
        <li>
          <Palette
            className=""
            value={f.color}
            onPick={(c) => { haptic.select(); setList("color")(c || ""); }}
          />
        </li>
      </List>

      <Choice
        caption="Что считаем"
        value={f.kind}
        onChange={(v) => setList("kind")(v)}
        options={GOAL_KINDS.map((k) => ({ value: k.id, label: k.label }))}
      />

      {f.kind === "income" ? (
        <div className="px-8 pt-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
          Доход меряем средним приходом за последние три месяца — той же цифрой,
          что стоит в кэшфлоу обзора.
        </div>
      ) : (
        <div className="mt-8">
          <div className="px-8 pb-1 text-[13px]" style={{ color: "var(--tg-hint)" }}>
            Какие счета считаем
          </div>
          <List strong inset className="!my-0">
            {accounts.map((a) => (
              <ListItem
                key={a.id}
                link
                chevron={false}
                colors={ITEM_COLORS}
                title={`${a.biz} / ${a.name}`}
                after={
                  (f.accounts || []).includes(a.id)
                    ? <Ion.checkmark size={20} style={{ color: "var(--tg-button)" }} />
                    : null
                }
                onClick={toggleAccount(a.id)}
              />
            ))}
          </List>
          <div className="px-8 pt-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
            Ничего не выбрано — считаем все деньги группы.
          </div>
        </div>
      )}

      {/* Порядок — это и есть правило: пока цель не закрыта, следующая не стартует */}
      <List strong inset>
        <ListInput type="select" value={f.position} onChange={set("position")} dropdown>
          <option value="">Место в лестнице · последнее</option>
          {places.map((n) => (
            <option key={n} value={n}>Место в лестнице · {n}-е</option>
          ))}
        </ListInput>
      </List>
    </>
  );
}

function RecurringFields({ f, set, setList, meta }) {
  return (
    <>
      <List strong inset>
        <ListInput type="text" placeholder="Название · аренда, зарплата" value={f.name} onChange={set("name")} />
        <ListInput type="text" placeholder="Заметка" value={f.note} onChange={set("note")} />
      </List>

      <SplitsEditor
        caption="С каких счетов платим"
        splits={f.splits}
        accounts={meta?.accounts || []}
        onChange={setList("splits")}
      />

      <DaysPicker caption="По каким числам платим" days={f.days} onChange={setList("days")} />
    </>
  );
}

function PaymentFields({ f, set, meta, isNew, onPaid, busy }) {
  return (
    <>
      <List strong inset>
        <ListInput type="text" placeholder="Клиент" value={f.client} onChange={set("client")} />
        <ListInput type="select" value={f.businessId} onChange={set("businessId")} dropdown>
          <option value="">Направление не задано</option>
          {(meta?.businesses || []).map((b) => <option key={b.id} value={b.id}>Направление · {b.name}</option>)}
        </ListInput>
        <ListInput type="text" placeholder="За что · внедрение, подписка" value={f.kind} onChange={set("kind")} />
        <ListInput type="number" inputMode="decimal" placeholder="Сумма, ₽" value={f.amount} onChange={set("amount")} />
        <ListInput type="number" inputMode="decimal" placeholder="Из них прибыль, ₽" value={f.profit} onChange={set("profit")} />
        <ListInput type="date" placeholder="Когда ждём" value={f.dueDate} onChange={set("dueDate")} />
        <ListInput type="text" placeholder="Заметка" value={f.note} onChange={set("note")} />
      </List>

      {/* Куда лягут деньги, когда придут. Пока не нажата «Оплачено»,
          платёж живёт только в прогнозе и в кэшфлоу не попадает. */}
      {!isNew && !f.received ? (
        <>
          <List strong inset>
            <ListInput type="select" value={f.account} onChange={set("account")} dropdown>
              <option value="">Основной счёт направления</option>
              {(meta?.accounts || []).map((a) => (
                <option key={a.id} value={a.id}>Счёт · {a.biz} / {a.name}</option>
              ))}
            </ListInput>
            <ListInput type="date" placeholder="Дата поступления" value={f.paidDate} onChange={set("paidDate")} />
          </List>

          <div className="px-4 pt-3">
            <Button large rounded onClick={onPaid} disabled={busy}
              colors={{ bgIos: "bg-[color:var(--fin-green)]" }}>
              Оплачено
            </Button>
          </div>
        </>
      ) : null}

      {!isNew && f.received ? (
        <div className="px-4 pt-3 text-center text-[13px]" style={{ color: "var(--fin-green)" }}>
          Оплачено — поступление уже в журнале операций
        </div>
      ) : null}
    </>
  );
}

function InflowFields({ f, set, isNew }) {
  return (
    <List strong inset>
      <ListInput type="text" placeholder="Месяц · 2026-08" value={f.month} onChange={set("month")} disabled={!isNew} />
      <ListInput type="number" inputMode="decimal" placeholder="Пришло за месяц, ₽" value={f.v} onChange={set("v")} />
    </List>
  );
}

/* Выбор одним касанием: Konsta Segmented, скруглённый.
   Больше трёх вариантов — блок прокручивается вбок, иначе подписи схлопнутся.
   `tone` у варианта красит бегунок под выбранным: расход красный, доход зелёный —
   та же финансовая семантика, что и у сумм в журнале. */
const TONE_BG = {
  green: "bg-[color:var(--fin-green)]",
  red: "bg-[color:var(--fin-red)]",
};

function Choice({ caption, value, options, onChange }) {
  const scroll = options.length > 3;
  const tone = options.find((o) => o.value === value)?.tone;
  /* mt-8 — тот же отступ, что Konsta ставит спискам (my-8): у соседей
     margin схлопывается, и шаг между блоками формы получается одинаковым. */
  return (
    <div className="mt-8">
      <div className="px-8 pb-1.5 text-[13px]" style={{ color: "var(--tg-hint)" }}>
        {caption}
      </div>
      <div className={scroll ? "hscroll px-4" : "px-4"}>
        <Segmented
          strong
          rounded
          className={scroll ? "!min-w-max" : ""}
          colors={tone ? { strongHighlightBgIos: TONE_BG[tone] } : undefined}
        >
          {options.map((o) => (
            <SegmentedButton
              key={o.value}
              active={value === o.value}
              colors={o.tone && value === o.value ? { segmentedStrongTextIos: "text-white" } : undefined}
              onClick={() => { haptic.select(); onChange(o.value); }}
            >
              <span className={scroll ? "px-2 whitespace-nowrap" : "whitespace-nowrap"}>{o.label}</span>
            </SegmentedButton>
          ))}
        </Segmented>
      </div>
    </div>
  );
}

/* Операция: категории зависят от типа, поэтому список пересобирается на лету,
   а несовместимая категория сбрасывается — иначе расход можно было бы записать
   в «Выручка», и P&L поехал бы. */
function OperationFields({ f, set, setF, toggle, meta, canSwitch = true }) {
  /* Перевод — не доход и не расход: категории, проекта и контрагента у него нет,
     зато есть второй счёт. Поэтому форма для него короче, а поля, к переводу
     не относящиеся, просто не показываются */
  const transfer = f.type === TRANSFER;
  const cats = f.type === "Поступление" ? INCOME_CATS : EXPENSE_CATS;
  const projects = useFetch("/projects");

  React.useEffect(() => {
    if (f.cat && !cats.includes(f.cat)) setF((p) => ({ ...p, cat: "" }));
  }, [f.cat, cats, setF]);

  // Сменили тип на доход или расход — счёт-получатель больше не при делах
  React.useEffect(() => {
    if (!transfer && f.to) setF((p) => ({ ...p, to: "" }));
  }, [transfer, f.to, setF]);

  const pick = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  /* Разбивка по счетам. Включили — выбранный счёт становится первой долей,
     вторая пустая; выключили — остаётся первая доля счётом всей операции */
  const split = f.split && !transfer && !f.upcoming;
  const toggleSplit = () => {
    haptic.select();
    setF((p) => p.split
      ? { ...p, split: false, account: p.splits[0]?.acc || p.account }
      : {
        ...p,
        split: true,
        splits: p.splits.length > 1 ? p.splits : [{ acc: p.account, amount: "" }, { acc: "", amount: "" }],
      });
  };

  return (
    <>
      <List strong inset>
        <MoneyInput placeholder="Сумма, ₽" value={f.amount} onChange={set("amount")} />
        <ListInput type="date" value={f.date} onChange={set("date")} />
        {/* Счёт задаёт и бизнес операции. Счетов бывает много, поэтому список,
            а не переключатель: имя бизнеса при счёте отвечает, чьи это деньги */}
        {!f.upcoming && !split ? (
          <ListInput type="select" value={f.account} onChange={set("account")} dropdown>
            <option value="">{transfer ? "Откуда — счёт не выбран" : "Счёт не выбран"}</option>
            {(meta?.accounts || []).map((a) => (
              <option key={a.id} value={a.id}>{a.biz} / {a.name}</option>
            ))}
          </ListInput>
        ) : null}
        {/* Счёт-получатель: свой счёт из списка исключаем — перевод самому себе
            ничего не меняет, а сервер такую запись всё равно не примет */}
        {!f.upcoming && transfer ? (
          <ListInput type="select" value={f.to} onChange={set("to")} dropdown>
            <option value="">Куда — счёт не выбран</option>
            {(meta?.accounts || []).filter((a) => a.id !== f.account).map((a) => (
              <option key={a.id} value={a.id}>{a.biz} / {a.name}</option>
            ))}
          </ListInput>
        ) : null}
        {!f.upcoming && !transfer ? (
          <ListInput type="select" value={f.cat} onChange={set("cat")} dropdown>
            <option value="">Категория не выбрана</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </ListInput>
        ) : null}
      </List>

      {f.upcoming ? null : (
        <Choice
          caption="Тип операции"
          value={f.type}
          onChange={pick("type")}
          options={[
            { value: "Расход", label: "Расход", tone: "red" },
            { value: "Поступление", label: "Доход", tone: "green" },
            // Перевод между счетами не делится: у него и так два счёта
            ...(split || f.wasSplit ? [] : [{ value: TRANSFER, label: "Перевод" }]),
          ]}
        />
      )}

      {/* Один платёж с нескольких счетов: 70 тысяч разработчику — 35 с МЕРЫ,
          35 с Диалогика. В журнале это одна строка, на счетах — свои доли */}
      {!f.upcoming && !transfer ? (
        <List strong inset>
          <ListItem
            colors={ITEM_COLORS}
            title="Разделить между счетами"
            after={<Toggle checked={!!f.split} onChange={toggleSplit} />}
          />
        </List>
      ) : null}
      {split ? (
        <SplitsEditor
          caption={f.type === "Поступление" ? "На какие счета пришло" : "С каких счетов платим"}
          splits={f.splits}
          accounts={meta?.accounts || []}
          onChange={pick("splits")}
          total={f.amount}
        />
      ) : null}

      <List strong inset>
        {!f.upcoming && !transfer ? (
          <ListInput type="select" value={f.proj} onChange={set("proj")} dropdown>
            <option value="">Без привязки к проекту</option>
            {(projects.data || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </ListInput>
        ) : null}
        <ListInput
          type="textarea"
          placeholder={f.upcoming ? "За что ждём деньги" : transfer ? "Зачем переводим" : "Что за операция"}
          value={f.note}
          onChange={set("note")}
        />
      </List>

      {/* Тоггл внизу: деньги ещё не пришли — это не проводка, а ожидание.
          У заведённой операции его нет: превратить проводку в ожидание нельзя. */}
      {canSwitch && !transfer ? (
        <List strong inset>
          <ListItem
            colors={ITEM_COLORS}
            title="Предстоящий платёж"
            after={<Toggle checked={f.upcoming} onChange={toggle("upcoming")} />}
          />
        </List>
      ) : null}
    </>
  );
}

const PROJECT_KIND_LABEL = [
  ["agency", "Вид · агентский заказ"],
  ["accounting", "Вид · сопровождение клиента"],
  ["product", "Вид · свой продукт"],
];

/* Карточка разработки — тем же языком, что форма сделки: тип и люди чипами,
   бизнес — чипами с аватарками, заголовков над рядами нет. */
function DevTaskFields({ f, set, setF, meta, projects, isNew }) {
  // Бизнесы с заведённой воронкой разработки — только у них есть куда положить карточку
  const businesses = (meta?.businesses || []).filter((b) => devStagesOf(b).length);
  const business = businesses.find((b) => b.id === f.businessId);
  const stages = devStagesOf(business);
  const bizProjects = (projects || []).filter((p) => p.businessId === f.businessId);

  const pick = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <>
      {/* Тип — первым, цветами доски разработки: выбранный заливается целиком */}
      <div className="hscroll flex gap-2 px-4 pt-1">
        {DEV_KINDS.map((k) => {
          const color = DEV_KIND_META[k]?.color || "var(--tg-hint)";
          const active = f.kind === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => { haptic.select(); pick("kind")(active ? "" : k); }}
              className="flex-1 min-w-0 rounded-full px-4 py-1.5 text-[13px] font-bold whitespace-nowrap active:opacity-70"
              style={{
                background: active ? color : `color-mix(in srgb, ${color} 14%, transparent)`,
                color: active ? "#fff" : color,
              }}
            >
              {k}
            </button>
          );
        })}
      </div>

      <List strong inset>
        <ListInput type="text" placeholder="Что делаем" value={f.title} onChange={set("title")} />
      </List>

      {/* Бизнес — чипы с аватарками, придвинуты к полю названия */}
      <Chips
        className="-mt-6"
        items={businesses.map((b) => ({ id: b.id, label: b.name, avatar: b.avatar }))}
        value={f.businessId}
        onChange={(v) => { haptic.select(); pick("businessId")(v); }}
      />

      {/* Исполнитель — чипы с должностью */}
      <div className="hscroll flex gap-2 px-4 py-2">
        {(meta?.users || []).map((u) => {
          const active = f.who === u.name;
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => { haptic.select(); pick("who")(active ? "" : u.name); }}
              className="shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap active:opacity-70"
              style={{
                background: active ? "var(--tg-button)" : "var(--tg-section-bg)",
                color: active ? "var(--tg-button-text)" : "var(--tg-text)",
              }}
            >
              {u.name}
              {u.role ? <span className="font-medium" style={{ opacity: 0.65 }}> · {u.role}</span> : null}
            </button>
          );
        })}
      </div>

      <List strong inset>
        {/* У новой карточки этап подставит API — первый в воронке */}
        {!isNew ? (
          <ListInput type="select" value={f.stage} onChange={set("stage")} dropdown>
            {stages.map((s) => <option key={s} value={s}>Этап · {s}</option>)}
          </ListInput>
        ) : null}
        {bizProjects.length ? (
          <ListInput type="select" value={f.projectId} onChange={set("projectId")} dropdown>
            <option value="">Без проекта</option>
            {bizProjects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </ListInput>
        ) : null}
        <ListInput type="date" placeholder="Срок" value={f.dueDate} onChange={set("dueDate")} />
        <ListInput type="textarea" placeholder="Подробности" value={f.details} onChange={set("details")} />
      </List>

      {/* Файлы и фото прямо при заведении: раньше их можно было приложить только
          в готовой карточке, и человек возвращался к ней вторым заходом. У правки
          блока нет — там вложения живут на самой карточке, вместе с чек-листом */}
      {isNew ? (
        <Attachments
          items={f.atts || []}
          onAdd={(up) => setF((p) => ({ ...p, atts: [...(p.atts || []), up] }))}
          onRemove={(i) => setF((p) => ({ ...p, atts: (p.atts || []).filter((_, x) => x !== i) }))}
        />
      ) : null}
    </>
  );
}

function DealFields({ f, set, setF, meta, isNew = true }) {
  // Бизнесы, у которых заведена воронка этой сделки — продаж или партнёров
  const funnels = (meta?.businesses || []).filter((b) => dealStagesOf(b, f.stream).length);
  const users = meta?.users || [];

  /* Контакты со всех сделок: нового человека можно не вписывать руками,
     а взять уже заведённого — в сделку ляжет своя копия его карточки.
     При правке блок скрыт, и список не грузим. */
  const deals = useFetch(isNew ? "/deals" : null);
  const contacts = React.useMemo(() => {
    // Один человек висит на нескольких сделках копиями — в поиске он нужен один раз
    const seen = new Set();
    const list = [];
    (deals.data || []).forEach((d) =>
      (d.contacts || []).forEach((c) => {
        const key = `${c.name}|${c.tg || ""}|${c.phone || ""}`.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        list.push({ ...c, company: d.company });
      })
    );
    return list;
  }, [deals.data]);

  const pick = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  // Поиск контакта: фильтруем на месте, показываем первые шесть совпадений
  const [contactQuery, setContactQuery] = useState("");
  const q = contactQuery.trim().toLowerCase();
  const foundContacts = q
    ? contacts.filter((c) => `${c.name} ${c.company}`.toLowerCase().includes(q)).slice(0, 6)
    : [];

  const pickContact = (v) => {
    haptic.select();
    const c = v && v !== "new" ? contacts.find((x) => String(x.id) === String(v)) : null;
    setF((p) => ({
      ...p,
      contactMode: v,
      contactName: c?.name || "",
      contactRole: c?.role || "",
      contactTg: c?.tg || "",
      contactPhone: c?.phone || "",
      contactEmail: c?.email || "",
      // Ссылки существующего контакта едут в копию целиком — в форме их не видно
      contactLinks: c?.links || [],
    }));
  };

  return (
    <>
      {/* Тир — первым, цветами тиров: выбранный заливается своим цветом целиком */}
      <div className="hscroll flex gap-2 px-4 pt-1">
        {TIERS.map((t) => {
          const m = TIER_META[t];
          const active = f.tier === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => { haptic.select(); pick("tier")(active ? "" : t); }}
              className="flex-1 min-w-0 rounded-full px-4 py-1.5 text-[13px] font-bold whitespace-nowrap active:opacity-70"
              style={{ background: active ? m.color : m.bg, color: active ? "#fff" : m.color }}
            >
              Тир {t}
            </button>
          );
        })}
      </div>

      <List strong inset>
        <ListInput type="text" placeholder="Компания" value={f.company} onChange={set("company")} />
      </List>

      {/* Воронка — чипы бизнесов с аватарками, как фильтр в CRM. Заголовков нет:
          аватарки и имена людей сами говорят, что это за ряды. -mt съедает часть
          нижнего поля списка Konsta — иначе от поля компании до чипов дыра. */}
      <Chips
        className="-mt-6"
        items={funnels.map((b) => ({ id: b.id, label: b.name, avatar: b.avatar }))}
        value={f.funnel}
        onChange={(v) => { haptic.select(); pick("funnel")(v); }}
      />

      <div className="hscroll flex gap-2 px-4 py-2">
        {users.map((u) => {
          const active = f.owner === u.name;
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => { haptic.select(); pick("owner")(active ? "" : u.name); }}
              className="shrink-0 rounded-full px-3.5 py-1.5 text-[13px] font-semibold whitespace-nowrap active:opacity-70"
              style={{
                background: active ? "var(--tg-button)" : "var(--tg-section-bg)",
                color: active ? "var(--tg-button-text)" : "var(--tg-text)",
              }}
            >
              {u.name}
              {u.role ? <span className="font-medium" style={{ opacity: 0.65 }}> · {u.role}</span> : null}
            </button>
          );
        })}
      </div>

      {/* Контакт. Нативный select с десятками имён нечитаем, поэтому свой поиск:
          набираешь имя или компанию — под полем выпадают совпадения, тап
          подставляет карточку. Новый контакт заводится кнопкой в том же блоке.
          При правке блока нет: контакты живой сделки лежат в её карточке. */}
      {isNew ? (
      <List strong inset>
        {!f.contactMode ? (
          <>
            <ListInput
              type="text"
              placeholder="Поиск контакта"
              value={contactQuery}
              onChange={(e) => setContactQuery(e.target.value)}
            />
            {foundContacts.map((c) => (
              <ListItem
                key={c.id}
                link
                chevron={false}
                colors={ITEM_COLORS}
                title={c.name}
                after={c.company}
                onClick={() => { setContactQuery(""); pickContact(String(c.id)); }}
              />
            ))}
            {contactQuery.trim() && !foundContacts.length ? (
              <ListItem colors={ITEM_COLORS} title="Никого не нашлось" />
            ) : null}
            <ListButton onClick={() => { haptic.select(); pickContact("new"); }}>
              Новый контакт
            </ListButton>
          </>
        ) : (
          <>
            <ListInput type="text" placeholder="Имя" value={f.contactName} onChange={set("contactName")} />
            <ListInput type="text" placeholder="Должность · ЛПР, маркетолог" value={f.contactRole} onChange={set("contactRole")} />
            <ListInput type="text" placeholder="Telegram · @ник" value={f.contactTg} onChange={set("contactTg")}
              autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            <ListInput type="tel" inputMode="tel" placeholder="Телефон" value={f.contactPhone} onChange={set("contactPhone")} />
            <ListInput type="email" inputMode="email" placeholder="Почта" value={f.contactEmail} onChange={set("contactEmail")}
              autoCapitalize="none" autoCorrect="off" spellCheck={false} />
            <ListButton onClick={() => { haptic.select(); pickContact(""); }}>
              Убрать контакт
            </ListButton>
          </>
        )}
      </List>
      ) : null}

      <List strong inset>
        <ListInput type="text" placeholder="ЛПР" value={f.lpr} onChange={set("lpr")} />
        <ListInput type="text" placeholder="Источник лида" value={f.src} onChange={set("src")} />
        {/* Тип работ живёт на проекте сделки, а у партнёров проекта нет */}
        {f.stream !== "partners" ? (
          <ListInput type="text" placeholder="Тип работ" value={f.projType} onChange={set("projType")} />
        ) : null}
      </List>

      {/* Деньги: чек он же выручка сделки, прибыль сервер считает сам */}
      <List strong inset>
        <MoneyInput placeholder="Чек, ₽" value={f.revenue} onChange={set("revenue")} />
        <MoneyInput placeholder="Себестоимость, ₽" value={f.expenses} onChange={set("expenses")} />
        <MoneyInput placeholder="MRR, ₽ в месяц" value={f.mrr} onChange={set("mrr")} />
      </List>
    </>
  );
}

function ProjectFields({ f, set, setList, meta }) {
  return (
    <>
      <AvatarPicker value={f.avatar} onChange={setList("avatar")} />

      <List strong inset>
        <ListInput type="text" placeholder="Название проекта" value={f.name} onChange={set("name")} />
        <ListInput type="select" value={f.kind} onChange={set("kind")} dropdown>
          {PROJECT_KIND_LABEL.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </ListInput>
        <ListInput type="select" value={f.businessId} onChange={set("businessId")} dropdown>
          <option value="">Бизнес не выбран</option>
          {(meta?.businesses || []).map((b) => <option key={b.id} value={b.id}>Бизнес · {b.name}</option>)}
        </ListInput>
        <ListInput type="text" placeholder="Тип работ · внедрение, сайт" value={f.type} onChange={set("type")} />
        <ListInput type="text" placeholder="Ответственный" value={f.owner} onChange={set("owner")} />
      </List>

      <List strong inset>
        <ListInput type="number" inputMode="decimal" placeholder="Чек проекта, ₽" value={f.income} onChange={set("income")} />
        <ListInput type="number" inputMode="decimal" placeholder="Себестоимость, ₽" value={f.expenses} onChange={set("expenses")} />
        <ListInput type="number" inputMode="decimal" placeholder="Объём работ, ₽ — если считаете" value={f.volume} onChange={set("volume")} />
      </List>
    </>
  );
}

/* Контакт сделки. Ссылки — списком: сайт, соцсети, календарь, всё, что не легло
   в отдельные поля. Клавиатуре запрещаем «помогать» там, где она портит ники и адреса. */
function ContactFields({ f, set, setList }) {
  const raw = { autoCapitalize: "none", autoCorrect: "off", spellCheck: false };
  return (
    <>
      <List strong inset>
        <ListInput type="text" placeholder="Имя" value={f.name} onChange={set("name")} />
        <ListInput type="text" placeholder="Должность · ЛПР, маркетолог" value={f.role} onChange={set("role")} />
      </List>

      <List strong inset>
        <ListInput type="text" placeholder="Telegram · @ник" value={f.tg} onChange={set("tg")} {...raw} />
        <ListInput type="tel" inputMode="tel" placeholder="Телефон" value={f.phone} onChange={set("phone")} />
        <ListInput type="email" inputMode="email" placeholder="Почта" value={f.email} onChange={set("email")} {...raw} />
      </List>

      <ItemsEditor
        caption="Ссылки"
        placeholder="Сайт, соцсеть, календарь"
        items={f.links}
        onChange={setList("links")}
      />
    </>
  );
}

/* Доступ к сервису. Логин и пароль показываем как есть: в хранилище приходят,
   чтобы прочитать и скопировать, а звёздочки этому только мешают. Клавиатуре
   запрещаем «помогать» — она портит и адрес, и пароль. */
function PasswordFields({ f, set }) {
  const raw = { autoCapitalize: "none", autoCorrect: "off", spellCheck: false };
  return (
    <List strong inset>
      <ListInput type="text" placeholder="Название · Яндекс Директ" value={f.name} onChange={set("name")} />
      <ListInput type="url" inputMode="url" placeholder="Ссылка" value={f.url} onChange={set("url")} {...raw} />
      <CopyInput placeholder="Логин" value={f.login} onChange={set("login")} />
      <CopyInput placeholder="Пароль" value={f.password} onChange={set("password")} />
    </List>
  );
}

/* Карта. Номер и CVV — те же поля с копированием, что у паролей: их переносят
   в форму оплаты, а не читают. Цифры набираются числовой клавиатурой, но тип
   поля текстовый — номер длиннее, чем безопасно доверять number. */

/* Срок — месяц и год как на самой карте. Косую черту ставит само поле:
   набираются только цифры (0631 → 06/31), на цифровой клавиатуре черты нет. */
const fmtExpiry = (raw) => {
  const d = String(raw).replace(/\D/g, "").slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

function CardFields({ f, set }) {
  const setExpiry = (e) => set("year")({ target: { value: fmtExpiry(e.target.value) } });
  return (
    <List strong inset>
      <CopyInput placeholder="Номер карты" value={f.number} onChange={set("number")} inputMode="numeric" numeric />
      <ListInput type="text" inputMode="numeric" placeholder="Срок · 06/31" value={f.year} onChange={setExpiry} />
      <CopyInput placeholder="CVV" value={f.cvv} onChange={set("cvv")} inputMode="numeric" numeric />
      <ListInput type="text" placeholder="Имя владельца" value={f.holder} onChange={set("holder")}
        autoCapitalize="characters" autoCorrect="off" spellCheck={false} />
    </List>
  );
}

/* Поле с копированием: за ним и приходят в хранилище, набирать пароль руками
   никто не станет. Konsta принимает готовое поле пропом `input` — кладём туда
   само поле и кнопку, она встаёт ровно там, где у Konsta крестик очистки.
   Получилось — на пару секунд показываем галочку вместо иконки. */
function CopyInput({ placeholder, value, onChange, inputMode, numeric = false }) {
  const [done, setDone] = useState(false);
  const timer = React.useRef(null);

  React.useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    if (!value) return;
    // Номер карты записывают группами, а вставляют в форму оплаты подряд
    if (!(await copyText(numeric ? value.replace(/\s+/g, "") : value))) {
      haptic.error();
      return;
    }
    haptic.success();
    setDone(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setDone(false), 1600);
  };

  const Icon = done ? Ion.checkmark : Ion.copy;

  return (
    <ListInput
      input={
        <div className="relative">
          <input
            type="text"
            inputMode={inputMode}
            className={`block w-full h-10 text-base appearance-none bg-transparent focus:outline-none pe-9 ${numeric ? "num" : ""}`}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            type="button"
            className="absolute end-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center active:opacity-40 disabled:opacity-25"
            style={{ color: done ? "var(--fin-green)" : "var(--tg-link)" }}
            onClick={copy}
            disabled={!value}
            aria-label={`Скопировать · ${placeholder}`}
          >
            <Icon size={19} />
          </button>
        </div>
      }
    />
  );
}
